// src/components/ImplementationPlanResults.tsx
import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ImplementationPlan } from '../types';
import NavigatorNav from './NavigatorNav';
import { LegalDisclaimer } from './LegalDisclaimer';
interface ImplementationPlanResultsProps {
  plan: ImplementationPlan;
  userEmail: string;
  onRestart: () => void;
}
const ImplementationPlanResults: React.FC<ImplementationPlanResultsProps> = ({
  plan,
  userEmail,
  onRestart,
}) => {
  const [expandedMonth, setExpandedMonth] = useState<number | null>(1);
  const toggleMonth = (monthNum: number) => {
    setExpandedMonth(expandedMonth === monthNum ? null : monthNum);
  };
  const handleDownloadPDF = () => {
    // TODO: Implement PDF generation
    alert('PDF download will be implemented with backend integration');
  };
  const handleBookCall = () => {
    // TODO: Link to Calendly or booking system
    window.open('https://calendly.com/your-link', '_blank');
  };
  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white">
      <NavigatorNav />
      <div className="py-8 px-4">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-8"
        >
          <div className="inline-block bg-green-100 text-green-700 px-4 py-2 rounded-full text-sm font-bold mb-4">
            ✅ YOUR PERSONALIZED PLAN IS READY
          </div>
          <h1 className="text-4xl font-bold text-gray-900 mb-2">
            Your 4-Month HIPAA Implementation Roadmap
          </h1>
          <p className="text-lg text-gray-600">
            Customized for {plan.organizationProfile.orgType} using {plan.organizationProfile.cloudProvider}
          </p>
          <p className="text-sm text-gray-500 mt-2">
            Plan sent to: <span className="font-semibold">{userEmail}</span>
          </p>
        </motion.div>
        {/* Organization Profile Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white rounded-2xl shadow-lg p-6 mb-6"
        >
          <h2 className="text-2xl font-bold text-gray-900 mb-4">📋 Your Profile</h2>
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <p className="text-sm text-gray-500">Organization Type</p>
              <p className="text-lg font-semibold text-gray-900">{plan.organizationProfile.orgType}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Cloud Provider</p>
              <p className="text-lg font-semibold text-gray-900">{plan.organizationProfile.cloudProvider}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Implementation Timeline</p>
              <p className="text-lg font-semibold text-gray-900">{plan.organizationProfile.timeline}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Current Stage</p>
              <p className="text-lg font-semibold text-gray-900">{plan.organizationProfile.stage}</p>
            </div>
          </div>
          {plan.organizationProfile.usesAI && (
            <div className="mt-4 bg-purple-50 border border-purple-200 rounded-lg p-4">
              <p className="text-sm font-semibold text-purple-900 mb-1">🤖 AI/ML Integration Detected</p>
              <p className="text-sm text-purple-700">
                Your plan includes AI-specific safeguards and compliance requirements.
              </p>
            </div>
          )}
        </motion.div>
        {/* Estimates Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-white rounded-2xl shadow-lg p-6 mb-6"
        >
          <h2 className="text-2xl font-bold text-gray-900 mb-4">📊 Implementation Estimates</h2>
          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-blue-50 rounded-lg p-4">
              <p className="text-sm text-blue-600 font-semibold mb-2">⏱️ ESTIMATED TIME</p>
              <p className="text-3xl font-bold text-blue-900">{plan.summary.estimatedTime}</p>
              <p className="text-sm text-blue-700 mt-2">
                Based on your team size and approach
              </p>
            </div>
            <div className="bg-green-50 rounded-lg p-4">
              <p className="text-sm text-green-600 font-semibold mb-2">💰 ESTIMATED COST</p>
              <p className="text-3xl font-bold text-green-900">{plan.summary.estimatedCost}</p>
              <p className="text-sm text-green-700 mt-2">
                Includes tools, training, and infrastructure
              </p>
            </div>
          </div>
        </motion.div>
        {/* Implementation Roadmap */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-white rounded-2xl shadow-lg p-6 mb-6"
        >
          <h2 className="text-2xl font-bold text-gray-900 mb-4">🗓️ 4-Month Implementation Roadmap</h2>
          <p className="text-gray-600 mb-6">
            Click each month to expand and see detailed tasks. Follow this phased approach to build audit-ready compliance.
          </p>
          {/* Month 1: Foundation */}
          <div className="mb-4">
            <button
              onClick={() => toggleMonth(1)}
              className="w-full flex items-center justify-between bg-blue-50 hover:bg-blue-100 transition rounded-lg p-4 text-left"
            >
              <div className="flex items-center gap-3">
                <div className="bg-blue-500 text-white w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm">
                  1
                </div>
                <div>
                  <h3 className="text-lg font-bold text-gray-900">Month 1: Foundation</h3>
                  <p className="text-sm text-gray-600">{(plan.monthlyPhases[0]?.tasks || []).length} essential tasks</p>
                </div>
              </div>
              <svg
                className={`w-6 h-6 text-gray-600 transition-transform ${expandedMonth === 1 ? 'rotate-180' : ''}`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>
            <AnimatePresence>
              {expandedMonth === 1 ? (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  className="bg-gray-50 rounded-b-lg p-4 mt-2"
                >
                  <ul className="space-y-3">
                    {(plan.monthlyPhases[0]?.tasks || []).map((item, index) => (
                      <li key={index} className="flex items-start gap-3">
                        <div className="bg-blue-500 text-white w-6 h-6 rounded flex items-center justify-center flex-shrink-0 text-xs font-bold mt-0.5">
                          {index + 1}
                        </div>
                        <div className="flex-1">
                          <p className="font-medium text-gray-900">{item.task}</p>
                          {item.action && <p className="text-sm text-gray-600 mt-1">{item.action}</p>}
                        </div>
                      </li>
                    ))}
                  </ul>
                </motion.div>
              ) : null}
            </AnimatePresence>
          </div>
          {/* Month 2: Administrative Safeguards */}
          <div className="mb-4">
            <button
              onClick={() => toggleMonth(2)}
              className="w-full flex items-center justify-between bg-green-50 hover:bg-green-100 transition rounded-lg p-4 text-left"
            >
              <div className="flex items-center gap-3">
                <div className="bg-green-500 text-white w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm">
                  2
                </div>
                <div>
                  <h3 className="text-lg font-bold text-gray-900">Month 2: Administrative Safeguards</h3>
                  <p className="text-sm text-gray-600">{(plan.monthlyPhases[1]?.tasks || []).length} policy & training tasks</p>
                </div>
              </div>
              <svg
                className={`w-6 h-6 text-gray-600 transition-transform ${expandedMonth === 2 ? 'rotate-180' : ''}`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>
            <AnimatePresence>
              {expandedMonth === 2 ? (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  className="bg-gray-50 rounded-b-lg p-4 mt-2"
                >
                  <ul className="space-y-3">
                    {(plan.monthlyPhases[1]?.tasks || []).map((item, index) => (
                      <li key={index} className="flex items-start gap-3">
                        <div className="bg-green-500 text-white w-6 h-6 rounded flex items-center justify-center flex-shrink-0 text-xs font-bold mt-0.5">
                          {index + 1}
                        </div>
                        <div className="flex-1">
                          <p className="font-medium text-gray-900">{item.task}</p>
                          {item.action && <p className="text-sm text-gray-600 mt-1">{item.action}</p>}
                        </div>
                      </li>
                    ))}
                  </ul>
                </motion.div>
              
              ) : null}
            </AnimatePresence>
          </div>
          {/* Month 3: Technical Safeguards */}
          <div className="mb-4">
            <button
              onClick={() => toggleMonth(3)}
              className="w-full flex items-center justify-between bg-purple-50 hover:bg-purple-100 transition rounded-lg p-4 text-left"
            >
              <div className="flex items-center gap-3">
                <div className="bg-purple-500 text-white w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm">
                  3
                </div>
                <div>
                  <h3 className="text-lg font-bold text-gray-900">Month 3: Technical Safeguards</h3>
                  <p className="text-sm text-gray-600">{(plan.monthlyPhases[2]?.tasks || []).length} security & infrastructure tasks</p>
                </div>
              </div>
              <svg
                className={`w-6 h-6 text-gray-600 transition-transform ${expandedMonth === 3 ? 'rotate-180' : ''}`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>
            <AnimatePresence>
              {expandedMonth === 3 ? (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                className="bg-gray-50 rounded-b-lg p-4 mt-2"
              >
                <ul className="space-y-3">
                  {(plan.monthlyPhases[2]?.tasks || []).map((item, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <div className="bg-purple-500 text-white w-6 h-6 rounded flex items-center justify-center flex-shrink-0 text-xs font-bold mt-0.5">
                        {index + 1}
                      </div>
                      <div className="flex-1">
                        <p className="font-medium text-gray-900">{item.task}</p>
                        {item.action && <p className="text-sm text-gray-600 mt-1">{item.action}</p>}
                      </div>
                    </li>
                  ))}
                </ul>
              </motion.div>
              
            ) : null}
            </AnimatePresence>
          </div>
          {/* Month 4: Documentation & Audit Prep */}
          <div className="mb-4">
            <button
              onClick={() => toggleMonth(4)}
              className="w-full flex items-center justify-between bg-orange-50 hover:bg-orange-100 transition rounded-lg p-4 text-left"
            >
              <div className="flex items-center gap-3">
                <div className="bg-orange-500 text-white w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm">
                  4
                </div>
                <div>
                  <h3 className="text-lg font-bold text-gray-900">Month 4: Documentation & Audit Prep</h3>
                  <p className="text-sm text-gray-600">{(plan.monthlyPhases[3]?.tasks || []).length} final preparation tasks</p>
                </div>
              </div>
              <svg
                className={`w-6 h-6 text-gray-600 transition-transform ${expandedMonth === 4 ? 'rotate-180' : ''}`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>
            <AnimatePresence>
              {expandedMonth === 4 ? (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                className="bg-gray-50 rounded-b-lg p-4 mt-2"
              >
                <ul className="space-y-3">
                  {(plan.monthlyPhases[3]?.tasks || []).map((item, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <div className="bg-orange-500 text-white w-6 h-6 rounded flex items-center justify-center flex-shrink-0 text-xs font-bold mt-0.5">
                        {index + 1}
                      </div>
                      <div className="flex-1">
                        <p className="font-medium text-gray-900">{item.task}</p>
                        {item.action && <p className="text-sm text-gray-600 mt-1">{item.action}</p>}
                      </div>
                    </li>
                  ))}
                </ul>
              </motion.div>
              
            ) : null}
            </AnimatePresence>
          </div>
        </motion.div>
        {/* Resources Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="bg-white rounded-2xl shadow-lg p-6 mb-6"
        >
          <h2 className="text-2xl font-bold text-gray-900 mb-4">📚 Implementation Resources</h2>
          <div className="grid md:grid-cols-3 gap-4">
            <div className="bg-gray-50 rounded-lg p-4">
              <div className="text-2xl mb-2">📄</div>
              <h3 className="font-semibold text-gray-900 mb-1">Policy Templates</h3>
              <p className="text-sm text-gray-600">HIPAA-compliant policy templates for your organization</p>
            </div>
            <div className="bg-gray-50 rounded-lg p-4">
              <div className="text-2xl mb-2">☁️</div>
              <h3 className="font-semibold text-gray-900 mb-1">{plan.organizationProfile.cloudProvider} Guides</h3>
              <p className="text-sm text-gray-600">Step-by-step {plan.organizationProfile.cloudProvider} configuration for HIPAA</p>
            </div>
            <div className="bg-gray-50 rounded-lg p-4">
              <div className="text-2xl mb-2">✅</div>
              <h3 className="font-semibold text-gray-900 mb-1">Checklists</h3>
              <p className="text-sm text-gray-600">Week-by-week checklists to track your progress</p>
            </div>
          </div>
        </motion.div>
        {/* CTA Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="bg-gradient-to-r from-blue-600 to-blue-700 rounded-2xl shadow-lg p-8 text-white text-center mb-6"
        >
          <h2 className="text-3xl font-bold mb-3">Need Help Implementing?</h2>
          <p className="text-lg text-blue-100 mb-6">
            Get expert guidance on building your HIPAA-compliant infrastructure
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={handleDownloadPDF}
              className="bg-white text-blue-600 px-8 py-3 rounded-lg font-semibold hover:bg-blue-50 transition"
            >
              📥 Download Full Plan (PDF)
            </button>
            <button
              onClick={handleBookCall}
              className="bg-blue-800 text-white px-8 py-3 rounded-lg font-semibold hover:bg-blue-900 transition"
            >
              📞 Book Planning Call (Free)
            </button>
          </div>
        </motion.div>
        {/* Footer Actions */}
        <div className="text-center">
          <button
            onClick={onRestart}
            className="text-gray-600 hover:text-gray-900 font-medium text-sm underline"
          >
            ← Start Over
          </button>
        </div>

        {/* Post-Assessment Disclaimer */}
        <LegalDisclaimer type="post-assessment" />

        {/* Footer Branding */}
        <div className="border-t-2 border-gray-200 mt-12 pt-8 text-center bg-gray-50 rounded-lg p-6">
          <p className="text-sm text-gray-600 mb-1">Built by Infinitra Innovations</p>
          <p className="text-base font-semibold text-gray-900 mb-3">Saida Babu Chanda & Yogananda Karra</p>
          <div className="flex items-center justify-center gap-4 text-sm">
            <a href="https://healthtechcompliance.com/#about" className="text-blue-600 hover:text-blue-700 font-medium">
              About the Initiative
            </a>
            <span className="text-gray-400">•</span>
            <a href="https://healthtechcompliance.com/#contribute" className="text-blue-600 hover:text-blue-700 font-medium">
              Contribute
            </a>
          </div>
        </div>
      </div>
      </div>
    </div>
  );
};
export default ImplementationPlanResults;
